import React, { Fragment, useState, useRef, useEffect } from "react";
import { formatCurrency } from "../../utils/helpers";
import {
  CURRENT_MONTH_SPOTLIGHT_TD,
  PRIOR_MONTH_SPOTLIGHT_TD,
  PRIOR_MONTH_SPOTLIGHT_TH,
  formatInvoiceDateShort,
  closedAccountTooltip,
  formatMonthLongYm,
  formatMonthShortYm,
  invoiceRowKey,
  isInvoiceRowEditing,
  rowBackgroundClass,
  invoiceTableRowGroups,
} from "../../utils/invoiceMonthDisplay";
import { ArrowDownIcon, ArrowUpIcon, DownloadIcon, EditIcon, TrashIcon } from "../icons";
import InvoiceRowEditPanel from "./InvoiceRowEditPanel";
import InvoiceStatusCell from "./InvoiceStatusCell";
import { formatGlAllocationsLabel } from "../../utils/glCodes";

import { createPortal } from "react-dom";

// Full column count for the MoM comparison table (prior columns collapse in Focus mode,
// and the Credit column collapses unless "Display Credits" is checked).
const COMPARISON_COL_COUNT = 15;

const baseTh =
  "px-1.5 py-2 text-left text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--ink,#1c1c1a)] bg-[var(--sunken2,#fbfaf8)]";
const sortableTh = `${baseTh} cursor-pointer select-none hover:text-black`;

const SortArrows = ({ sortConfig, sortKey }) => {
  const isActive = sortConfig.key === sortKey;
  return (
    <span className="inline-flex flex-col ml-1 -space-y-1 text-[var(--faint,#9a9a95)] shrink-0">
      <ArrowUpIcon
        className={`w-2.5 h-2.5 ${isActive && sortConfig.direction === "asc" ? "text-[var(--accent,#a51c1c)]" : ""}`}
      />
      <ArrowDownIcon
        className={`w-2.5 h-2.5 ${isActive && sortConfig.direction === "desc" ? "text-[var(--accent,#a51c1c)]" : ""}`}
      />
    </span>
  );
};

const SortableTh = ({ children, sortKey, sortConfig, onSort, className = "", rowSpan }) => {
  const isActive = sortConfig.key === sortKey;
  return (
  <th
    rowSpan={rowSpan}
    onClick={() => onSort(sortKey)}
    className={`${sortableTh} ${isActive ? "text-[var(--accent,#a51c1c)]" : ""} ${className}`}
  >
    <span
      className={`inline-flex items-center ${
        className.includes("text-center") ? "justify-center w-full" : ""
      }`}
    >
      {children}
      <SortArrows sortConfig={sortConfig} sortKey={sortKey} />
    </span>
  </th>
  );
};

const ExternalSendIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
  </svg>
);

const SparkleNoteIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M9.813 2.659a1.5 1.5 0 012.374 0l1.22 1.84a1 1 0 00.757.424l2.2.255a1.5 1.5 0 01.831 2.548l-1.59 1.55a1 1 0 00-.287.884l.375 2.186a1.5 1.5 0 01-2.175 1.58l-1.967-.965a1 1 0 00-.878 0l-1.967.965a1.5 1.5 0 01-2.175-1.58l.375-2.186a1 1 0 00-.287-.884l-1.59-1.55a1.5 1.5 0 01.831-2.548l2.2-.255a1 1 0 00.757-.424l1.22-1.84z" />
  </svg>
);

const PlusIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const BanIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.343 6.343l11.314 11.314" />
  </svg>
);

const CircuitIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h4m4 0h8M4 12h8m4 0h4M4 17h2m4 0h10" />
    <circle cx="10" cy="7" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="14" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="8" cy="17" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const DotsIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <circle cx="4" cy="10" r="1.5" />
    <circle cx="10" cy="10" r="1.5" />
    <circle cx="16" cy="10" r="1.5" />
  </svg>
);

function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200;
      setCoords({
        top: openUp ? rect.top : rect.bottom,
        left: rect.right,
        openUp,
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200;
      setCoords({
        top: openUp ? rect.top : rect.bottom,
        left: rect.right,
        openUp,
      });
    }
    setOpen((p) => !p);
  };

  if (items.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`
          inline-flex items-center justify-center w-8 h-8 rounded-lg
          border border-stone-200 bg-white
          text-stone-400 transition-all duration-150
          hover:text-stone-700 hover:bg-stone-50 hover:border-stone-300
          focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400
          ${open ? "bg-stone-100 text-stone-700 border-stone-300" : ""}
        `}
      >
        <DotsIcon />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: coords.openUp ? undefined : coords.top + 6,
            bottom: coords.openUp ? window.innerHeight - coords.top + 6 : undefined,
            left: coords.left - 192,
            zIndex: 9999,
          }}
          className="
            w-48
            rounded-xl border border-stone-200/80
            bg-white shadow-lg shadow-stone-200/60
            ring-1 ring-black/[0.04]
            py-1 overflow-hidden
          "
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              disabled={item.disabled}
              onClick={() => { if (!item.disabled) { item.onClick(); setOpen(false); } }}
              className={`
                w-full flex items-center gap-2.5 px-3 py-2
                text-sm font-medium text-left
                transition-colors duration-100
                disabled:opacity-40 disabled:cursor-not-allowed
                ${item.danger
                  ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  : "text-stone-700 hover:bg-stone-50 hover:text-stone-900"
                }
              `}
            >
              {item.icon && (
                <span className={`flex-shrink-0 ${item.danger ? "text-rose-500" : "text-stone-400"}`}>
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

const GlCodeCell = ({ glCode, allocations }) => {
  const value = formatGlAllocationsLabel(allocations, glCode);
  if (!value) {
    return (
      <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">
        No GL
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-md bg-stone-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-stone-700 ring-1 ring-inset ring-stone-200"
      title="GL code"
    >
      {value}
    </span>
  );
};

const InvoicesMonthComparisonTable = ({
  comparison,
  rows,
  sortConfig,
  onSort,
  editingInvoiceId,
  editInvoiceDate,
  setEditInvoiceDate,
  editInvoiceNumber,
  setEditInvoiceNumber,
  editGlCode,
  setEditGlCode,
  editGlAllocations = [],
  setEditGlAllocations,
  editGlSplit = false,
  setEditGlSplit,
  glCodes = [],
  editMonthlyMrc,
  setEditMonthlyMrc,
  editTotalAmount,
  setEditTotalAmount,
  editCredit,
  setEditCredit,
  editTaxesAndSurcharges,
  setEditTaxesAndSurcharges,
  editDueDate,
  setEditDueDate,
  editNote,
  setEditNote,
  savingInvoiceId,
  generatingNoteId,
  renderChangeCell,
  renderNoteCell,
  canEditInvoice,
  canDeleteInvoice,
  canCloseAccount,
  canAddInvoice,
  onAddMissing,
  onDownload,
  downloadingId,
  deletingId,
  onSaveEdit,
  onCancelEdit,
  onGenerateNote,
  canShowGenerateNote,
  onMarkReviewed,
  canShowMarkReviewed,
  onOpenDelete,
  onOpenClose,
  beginEditInvoice,
  canSendEmail,
  selectedEmailIds,
  onToggleEmailSelect,
  onSendEmail,
  onResendEmail,
  sendingEmailId,
  formatEmailSentDate,
  onToggleSelectAll,
  tableTotals,
  viewMode = "compare",
  showCreditColumn = false,
  showInvoiceNumberColumn = false,
  noteAutomationSettings,
  onViewCircuits,
}) => {
  const isCompare = viewMode !== "focus";
  const showCredit = showCreditColumn;
  const showInvoiceNumber = showInvoiceNumberColumn;
  const colSpanCount =
    10 + (isCompare ? 1 : 0) + (showInvoiceNumber ? 1 : 0) + (showCredit ? 1 : 0);
  const closeAccountItems = (inv) =>
    canCloseAccount
      ? [
          {
            label: "Close account",
            icon: <BanIcon className="w-4 h-4" />,
            onClick: () => onOpenClose?.(inv),
          },
        ]
      : [];
  const viewCircuitsItems = (inv) => {
    const account = String(inv?.billing_account || "").trim();
    if (!account || typeof onViewCircuits !== "function") return [];
    return [
      {
        label: "View circuit on this account",
        icon: <CircuitIcon className="w-4 h-4" />,
        onClick: () => onViewCircuits(account),
      },
    ];
  };
  // check + provider/account + [prior] + mrc + total + inv + [credit] + change + note + send + actions

  const priorLabel = formatMonthShortYm(comparison.priorMonth);
  const currentLabel = formatMonthShortYm(comparison.invoiceMonth);
  const priorMonthTitle = formatMonthLongYm(comparison.priorMonth);
  const currentMonthTitle = formatMonthLongYm(comparison.invoiceMonth);

  const selectableOnPage = rows.filter((r) => r.row_kind !== "missing" && r.id != null);
  const allPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((r) => selectedEmailIds?.has(r.id));

  const colWidths = isCompare
    ? showCredit
      ? {
          check: "2%",
          provider: "14%",
          gl: "7%",
          status: "11%",
          prior: "8%",
          mrc: "8%",
          total: "8%",
          inv: "6%",
          credit: "7%",
          change: "8%",
          note: "4%",
          send: "10%",
          actions: "7%",
        }
      : {
          check: "3%",
          provider: "16%",
          gl: "7%",
          status: "12%",
          prior: "9%",
          mrc: "9%",
          total: "9%",
          inv: "7%",
          change: "8%",
          note: "4%",
          send: "9%",
          actions: "7%",
        }
    : showCredit
      ? {
          check: "3%",
          provider: "16%",
          gl: "7%",
          status: "12%",
          mrc: "9%",
          total: "8%",
          inv: "7%",
          credit: "7%",
          change: "9%",
          note: "4%",
          send: "11%",
          actions: "7%",
        }
      : {
          check: "3%",
          provider: "18%",
          gl: "7%",
          status: "13%",
          mrc: "10%",
          total: "10%",
          inv: "7%",
          change: "10%",
          note: "4%",
          send: "11%",
          actions: "7%",
        };

  return (
    <table className="w-full table-fixed border-collapse text-sm">
      <colgroup>
        <col style={{ width: colWidths.check }} />
        <col style={{ width: colWidths.provider }} />
        <col style={{ width: colWidths.gl }} />
        <col style={{ width: colWidths.status }} />
        {isCompare && <col style={{ width: colWidths.prior }} />}
        <col style={{ width: colWidths.mrc }} />
        <col style={{ width: colWidths.total }} />
        {showInvoiceNumber && <col style={{ width: colWidths.inv }} />}
        {showCredit && <col style={{ width: colWidths.credit }} />}
        <col style={{ width: colWidths.change }} />
        <col style={{ width: colWidths.note }} />
        <col style={{ width: colWidths.send }} />
        <col style={{ width: colWidths.actions }} />
      </colgroup>
      <thead className="sticky top-0 z-20">
        <tr className="bg-white">
          <th rowSpan={2} className={`${baseTh} align-bottom pb-2 pl-2 bg-white border-b border-stone-200`}>
            {canSendEmail && (
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={() => onToggleSelectAll?.(selectableOnPage.map((r) => r.id))}
                disabled={sendingEmailId != null || selectableOnPage.length === 0}
                className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                title="Select all on this page"
              />
            )}
          </th>
          <SortableTh
            sortKey="provider"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white whitespace-nowrap border-b border-stone-200"
          >
            Account
          </SortableTh>
          <SortableTh
            sortKey="gl_code"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white whitespace-nowrap border-b border-stone-200"
          >
            GL
          </SortableTh>
          <SortableTh
            sortKey="row_kind"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white whitespace-nowrap border-b border-stone-200"
          >
            Status
          </SortableTh>
          {isCompare && (
            <th
              className="px-1.5 py-1.5 text-center text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--ink,#1c1c1a)] bg-[var(--band2,#f4f1e8)]"
            >
              <span>
                {priorMonthTitle
                  ? `${priorMonthTitle} · Prior`
                  : `${priorLabel} · Prior`}
              </span>
            </th>
          )}
          <th
  colSpan={2 + (showInvoiceNumber ? 1 : 0) + (showCredit ? 1 : 0)}
  className="px-2 py-1.5 text-center text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--ink,#1c1c1a)] bg-[var(--band,#eef3fa)] border-b border-[var(--line,#e4e2dc)]"
>
            <span>
              {currentMonthTitle
                ? `${currentMonthTitle} · Current`
                : `${currentLabel} · Current`}
            </span>
          </th>
          <SortableTh
            sortKey="mrc_change"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white text-center border-b border-stone-200"
          >
            Change
          </SortableTh>
          <SortableTh
            sortKey="note"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white text-center border-b border-stone-200"
          >
            Note
          </SortableTh>
          <SortableTh
            sortKey="email_sent_at"
            sortConfig={sortConfig}
            onSort={onSort}
            rowSpan={2}
            className="align-bottom pb-2 bg-white text-center leading-tight border-b border-stone-200"
          >
            <span className="inline-flex flex-col items-center leading-tight" title="Send to AP">
              <span>Send</span>
              <span>to AP</span>
            </span>
          </SortableTh>
          <th
            rowSpan={2}
            className={`${baseTh} text-right align-bottom pb-2 pr-2 whitespace-nowrap bg-white border-b border-stone-200`}
          >
            Actions
          </th>
        </tr>
        <tr className="border-b border-stone-200 bg-white">
          {isCompare && (
            <SortableTh
              sortKey="prior_monthly_mrc"
              sortConfig={sortConfig}
              onSort={onSort}
              className={`${PRIOR_MONTH_SPOTLIGHT_TH} text-center justify-items-center`}
            >
              Total
              <br />
              Amount
            </SortableTh>
          )}
          <th
            onClick={() => onSort("monthly_mrc")}
            className={`px-2 py-2 text-[12px] font-semibold uppercase tracking-[0.04em] bg-[var(--band,#eef3fa)] cursor-pointer select-none text-center ${
              sortConfig.key === "monthly_mrc" ? "text-[var(--accent,#a51c1c)]" : "text-[var(--ink,#1c1c1a)]"
            }`}
          >
            <span className="inline-flex items-center justify-center w-full">
              MRC
              <SortArrows sortConfig={sortConfig} sortKey="monthly_mrc" />
            </span>
          </th>
          <th
            onClick={() => onSort("total_invoice_amount")}
            className={`px-1.5 py-2 text-[12px] font-semibold uppercase tracking-[0.04em] bg-[var(--band,#eef3fa)] cursor-pointer select-none text-center ${
              sortConfig.key === "total_invoice_amount" ? "text-[var(--accent,#a51c1c)]" : "text-[var(--ink,#1c1c1a)]"
            }`}
          >
            <span className="inline-flex items-center justify-center w-full leading-tight text-center">
              Total
              <br />
              Amount
              <SortArrows sortConfig={sortConfig} sortKey="total_invoice_amount" />
            </span>
          </th>
          {showInvoiceNumber && (
            <th
              onClick={() => onSort("invoice_number")}
              className={`px-1.5 py-2 text-[12px] font-semibold uppercase tracking-[0.04em] bg-[var(--band,#eef3fa)] cursor-pointer select-none text-center ${
                sortConfig.key === "invoice_number" ? "text-[var(--accent,#a51c1c)]" : "text-[var(--ink,#1c1c1a)]"
              }`}
            >
              <span className="inline-flex items-center justify-center w-full leading-tight text-center">
                Invoice
                <br />
                No.
                <SortArrows sortConfig={sortConfig} sortKey="invoice_number" />
              </span>
            </th>
          )}
          {showCredit && (
            <th
              onClick={() => onSort("credit_amount")}
              className={`px-1.5 py-2 text-[12px] font-semibold uppercase tracking-[0.04em] bg-[var(--band,#eef3fa)] cursor-pointer select-none text-center ${
                sortConfig.key === "credit_amount" ? "text-[var(--accent,#a51c1c)]" : "text-[var(--ink,#1c1c1a)]"
              }`}
            >
              <span className="inline-flex items-center justify-center w-full">
                Credit
                <SortArrows sortConfig={sortConfig} sortKey="credit_amount" />
              </span>
            </th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-100">
        {invoiceTableRowGroups(rows, sortConfig?.key).map((group) => (
          <Fragment key={`provider-${group.provider ?? "sorted"}`}>
            {group.provider != null && (
            <tr className="bg-[var(--sunken2,#fbfaf8)]">
              <td className="pl-2 py-2" />
              <td
                colSpan={Math.max(1, colSpanCount - 1)}
                className="px-1.5 py-2 align-middle"
              >
                <span className="font-semibold text-[var(--ink,#1c1c1a)]">{group.provider}</span>
                <span className="ml-1.5 text-[12px] text-[var(--muted,#6b6a66)]">
                  {group.accountCount} {group.accountCount === 1 ? "account" : "accounts"} · {group.receivedCount} received
                </span>
              </td>
            </tr>
            )}
        {group.items.map((inv) => {
          const isEditing = isInvoiceRowEditing(editingInvoiceId, inv);
          const rowClass = [
            rowBackgroundClass(inv.row_kind),
            isEditing ? "ring-2 ring-inset ring-sky-400" : "hover:bg-stone-50/80",
          ]
            .filter(Boolean)
            .join(" ");

          const sentLabel = formatEmailSentDate?.(inv.email_sent_at);
          const canSelect = inv.row_kind !== "missing" && inv.id != null && !isEditing;
          const isSending = sendingEmailId === inv.id;
          const glForDisplay = isEditing
            ? formatGlAllocationsLabel(editGlAllocations, editGlCode)
            : formatGlAllocationsLabel(inv.gl_allocations, inv.gl_code);
          const isAccountClosed = inv.account_is_active === false;
          const closedTooltip = closedAccountTooltip(inv);

          return (
            <Fragment key={invoiceRowKey(inv)}>
              <tr className={rowClass} title={closedTooltip || undefined}>
                <td className="pl-2 py-2.5 align-middle">
                  {canSelect && canSendEmail ? (
                    <input
                      type="checkbox"
                      checked={selectedEmailIds?.has(inv.id)}
                      onChange={() => onToggleEmailSelect?.(inv.id)}
                      disabled={sendingEmailId != null}
                      className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                    />
                  ) : (
                    <span className="inline-block w-4" aria-hidden="true" />
                  )}
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <div
                    className={`tabular-nums break-all leading-snug text-[var(--ink,#1c1c1a)] min-w-0 ${
                      group.provider != null ? "pl-4" : ""
                    }`}
                  >
                    {group.provider == null && inv.provider ? (
                      <div className="text-[11px] text-[var(--muted,#6b6a66)]">{inv.provider}</div>
                    ) : null}
                    <div className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="min-w-0 break-all">
                      {inv.billing_account} {inv.display_name ? `- ${inv.display_name}` : ""}
                    </span>
                    {isAccountClosed && (
                      <span
                        className="inline-flex shrink-0 text-red-600"
                        title={closedTooltip || "Closed account"}
                        aria-label={closedTooltip || "Closed account"}
                      >
                        <BanIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                    </div>
                  </div>
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <GlCodeCell glCode={glForDisplay} />
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <InvoiceStatusCell inv={inv} noteSettings={noteAutomationSettings} />
                </td>
                {isCompare && (
                  <td className={`${PRIOR_MONTH_SPOTLIGHT_TD} text-center overflow-hidden align-top`}>
                    {inv.row_kind === "new" ? (
                      <span className="text-stone-400">—</span>
                    ) : (
                      <div className="leading-snug">
                        <div>
                          {inv.prior_total_invoice_amount != null ? (
                            formatCurrency(inv.prior_total_invoice_amount)
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                        </div>
                        <div className="text-stone-400">
                          {formatInvoiceDateShort(inv.prior_invoice_date)}
                        </div>
                      </div>
                    )}
                  </td>
                )}
                <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} text-center overflow-hidden align-top pl-2`}>
                  {inv.row_kind === "missing" ? (
                    <span className="text-stone-400">—</span>
                  ) : inv.id == null && inv.row_kind === "new" ? (
                    <span className="font-normal text-stone-400">—</span>
                  ) : (
                    <div className="leading-snug">
                      <div className="font-semibold text-stone-900">
                        {formatCurrency(
                          isEditing && editMonthlyMrc !== ""
                            ? Number(editMonthlyMrc) || 0
                            : inv.monthly_mrc ?? 0,
                        )}
                      </div>
                      <div className="font-normal text-stone-500">
                        {formatInvoiceDateShort(
                          isEditing && editInvoiceDate ? editInvoiceDate : inv.invoice_date,
                        )}
                      </div>
                    </div>
                  )}
                </td>

                <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} text-center font-semibold overflow-hidden tabular-nums align-top`}>
                  {inv.row_kind === "missing" ? (
                    <span className="text-stone-400">—</span>
                  ) : inv.id == null && inv.row_kind === "new" ? (
                    <span className="font-normal text-stone-400">—</span>
                  ) : (
                    formatCurrency(
                      isEditing && editTotalAmount !== ""
                        ? Number(editTotalAmount) || 0
                        : inv.total_invoice_amount ?? 0,
                    )
                  )}
                </td>
                {showInvoiceNumber && (
  <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} font-normal text-stone-600 text-center overflow-hidden break-all align-top`}>
    {inv.row_kind === "missing" ? (
      <span className="text-stone-400">—</span>
    ) : inv.id == null && inv.row_kind === "new" ? (
      <span className="text-stone-400">—</span>
    ) : (() => {
      const num = isEditing && editInvoiceNumber != null
        ? String(editInvoiceNumber).trim()
        : inv.invoice_number != null ? String(inv.invoice_number).trim() : "";
      return num || <span className="text-stone-400">—</span>;
    })()}
  </td>
)}
                {showCredit && (
                <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} font-normal text-stone-600 text-center overflow-hidden align-top`}>
                  {inv.row_kind === "missing" ? (
                    <span className="text-stone-400">—</span>
                  ) : inv.id == null && inv.row_kind === "new" ? (
                    <span className="text-stone-400">—</span>
                  ) : (() => {
                    const creditValue = isEditing && editCredit !== ""
                      ? Number(editCredit) || 0
                      : inv.credit_amount != null
                        ? Number(inv.credit_amount)
                        : null;
                    return creditValue != null && creditValue > 0 ? (
                      formatCurrency(creditValue)
                    ) : (
                      <span className="text-stone-400">—</span>
                    );
                  })()}
                </td>
                )}
                <td className="px-1.5 py-2.5 overflow-hidden align-top text-center">
                  {isEditing ? (
                    <span className="text-stone-400">—</span>
                  ) : (
                    renderChangeCell(inv)
                  )}
                </td>
                <td className="px-1.5 py-2.5 align-top overflow-hidden text-center">
                  {isEditing ? (
                    <span className="text-stone-400 text-xs italic">—</span>
                  ) : (
                    renderNoteCell(inv)
                  )}
                </td>
                <td className="px-1.5 py-2.5 align-top overflow-hidden text-center">
  {inv.row_kind === "missing" || (inv.row_kind === "new" && inv.id == null) ? (
    <span className="text-stone-400">—</span>
  ) : canSelect && canSendEmail ? (
    sentLabel ? (
      <span className="inline-flex items-center justify-center gap-1 text-sm font-medium text-emerald-700">
        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        {formatInvoiceDateShort(inv.email_sent_at)}
      </span>
    ) : (
      <button
        type="button"
        onClick={() => onSendEmail?.(inv.id)}
        disabled={isSending || sendingEmailId != null}
        className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
      >
        {isSending ? "Sending…" : "Send"}
        <ExternalSendIcon />
      </button>
    )
  ) : (
    <span className="text-stone-400">—</span>
  )}
</td>
                <td className="px-1.5 py-2.5 pr-2 text-right whitespace-nowrap overflow-hidden align-top">
                  {inv.row_kind === "missing" ? (
                    <ActionMenu
                      items={[
                        ...(canAddInvoice
                          ? [{ label: "Add invoice", icon: <PlusIcon className="w-4 h-4" />, onClick: () => onAddMissing(inv) }]
                          : []),
                        ...viewCircuitsItems(inv),
                        ...closeAccountItems(inv),
                      ]}
                    />
                  ) : inv.row_kind === "new" && inv.id == null ? (
                    <ActionMenu
                      items={[
                        ...(canAddInvoice
                          ? [{ label: "Add invoice", icon: <PlusIcon className="w-4 h-4" />, onClick: () => onAddMissing(inv) }]
                          : []),
                        ...viewCircuitsItems(inv),
                        ...closeAccountItems(inv),
                      ]}
                    />
                  ) : isEditing ? null : (
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onDownload(inv.id)}
                      disabled={downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id}
                      title="Download"
                      aria-label="Download"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 transition-all duration-150 hover:bg-sky-100 hover:border-sky-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <DownloadIcon className="w-4 h-4" />
                    </button>
                    <ActionMenu
                      items={[
                        // {
                        //   label: "Download",
                        //   icon: <DownloadIcon className="w-4 h-4" />,
                        //   disabled: downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id,
                        //   loading: downloadingId === inv.id,
                        //   onClick: () => onDownload(inv.id),
                        // },
                        ...(canEditInvoice
                          ? [{
                              label: "Edit invoice",
                              icon: <EditIcon className="w-4 h-4" />,
                              disabled: deletingId === inv.id || downloadingId === inv.id || editingInvoiceId != null,
                              onClick: () => beginEditInvoice(inv),
                            }]
                          : []),
                        ...(canShowMarkReviewed?.(inv)
                          ? [{
                              label: "Mark reviewed",
                              icon: <CheckIcon className="w-4 h-4" />,
                              disabled: deletingId === inv.id || downloadingId === inv.id || savingInvoiceId === inv.id,
                              onClick: () => onMarkReviewed?.(inv),
                            }]
                          : []),
                        ...(canSendEmail && sentLabel
                          ? [{
                              label: "Resend",
                              icon: <ExternalSendIcon className="w-4 h-4" />,
                              disabled: isSending || sendingEmailId != null,
                              onClick: () => onResendEmail?.(inv.id),
                            }]
                          : []),
                        ...(canDeleteInvoice
                          ? [{
                              label: "Delete invoice",
                              icon: <TrashIcon className="w-4 h-4" />,
                              disabled: downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id,
                              danger: true,
                              onClick: () => onOpenDelete(inv),
                            }]
                          : []),
                        ...viewCircuitsItems(inv),
                        ...closeAccountItems(inv),
                      ]}
                    />
                    </div>
                  )}
                </td>
              </tr>
              {isEditing && (
                <InvoiceRowEditPanel
                  invoice={inv}
                  editInvoiceDate={editInvoiceDate}
                  setEditInvoiceDate={setEditInvoiceDate}
                  editInvoiceNumber={editInvoiceNumber}
                  setEditInvoiceNumber={setEditInvoiceNumber}
                  editGlCode={editGlCode}
                  setEditGlCode={setEditGlCode}
                  editGlAllocations={editGlAllocations}
                  setEditGlAllocations={setEditGlAllocations}
                  editGlSplit={editGlSplit}
                  setEditGlSplit={setEditGlSplit}
                  glCodes={glCodes}
                  editMonthlyMrc={editMonthlyMrc}
                  setEditMonthlyMrc={setEditMonthlyMrc}
                  editTotalAmount={editTotalAmount}
                  setEditTotalAmount={setEditTotalAmount}
                  editCredit={editCredit}
                  setEditCredit={setEditCredit}
                  editTaxesAndSurcharges={editTaxesAndSurcharges}
                  setEditTaxesAndSurcharges={setEditTaxesAndSurcharges}
                  editDueDate={editDueDate}
                  setEditDueDate={setEditDueDate}
                  editNote={editNote}
                  setEditNote={setEditNote}
                  saving={savingInvoiceId === inv.id}
                  generatingNote={generatingNoteId === inv.id}
                  onSave={() => onSaveEdit(inv.id)}
                  onCancel={onCancelEdit}
                  onGenerateNote={
                    canEditInvoice && canShowGenerateNote?.(inv)
                      ? () => onGenerateNote(inv.id)
                      : undefined
                  }
                />
              )}
            </Fragment>
          );
        })}
          </Fragment>
        ))}
      </tbody>
      {tableTotals && (
        <tfoot className="sticky bottom-0 z-10 border-t-2 border-stone-300 bg-stone-50">
          <tr>
            <td colSpan={4} className="px-2 py-2.5 pl-2 text-sm font-semibold text-stone-900">
              Total ({tableTotals.count} {tableTotals.count === 1 ? "Record" : "Records"})
            </td>
            {isCompare && (
  <td className={`${PRIOR_MONTH_SPOTLIGHT_TD} text-center font-semibold text-stone-800`}>
    {formatCurrency(tableTotals.priorTotalInvoiceAmount ?? 0)}
  </td>
)}
            <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} text-center font-semibold text-sky-950 pl-2`}>
              {formatCurrency(tableTotals.currentMrc)}
            </td>
            <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} text-center font-semibold text-sky-950`}>
  {formatCurrency(tableTotals.currentTotalInvoiceAmount ?? 0)}
</td>
            {/* <td className={CURRENT_MONTH_SPOTLIGHT_TD} /> */}
            {showInvoiceNumber && <td className={CURRENT_MONTH_SPOTLIGHT_TD} />}
            {showCredit && (
            <td className={`${CURRENT_MONTH_SPOTLIGHT_TD} text-center font-semibold text-stone-700 !overflow-visible px-1`}>
              <span className="inline-block max-w-full break-all tabular-nums leading-snug">
                {tableTotals.credit > 0 ? formatCurrency(tableTotals.credit) : "—"}
              </span>
            </td>
            )}
            <td colSpan={3} />
          </tr>
        </tfoot>
      )}
    </table>
  );
};

export { SparkleNoteIcon };
export default InvoicesMonthComparisonTable;
